import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, AlertCircle, Clock, RefreshCw } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

interface WebhookEventsListProps {
  events: any[];
  loading: boolean;
}

export const WebhookEventsList: React.FC<WebhookEventsListProps> = ({ 
  events,
  loading
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  
  // Filter events based on search query and event type
  const filteredEvents = events.filter(event => {
    const matchesSearch = 
      event.event_id?.toLowerCase().includes(searchQuery.toLowerCase()) || 
      event.event_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.message?.toLowerCase().includes(searchQuery.toLowerCase());
      
    const matchesEventType = eventTypeFilter === 'all' || event.event_type?.includes(eventTypeFilter);
    
    return matchesSearch && matchesEventType;
  });
  
  // Get unique event types for the filter dropdown
  const uniqueEventTypes = Array.from(new Set(events.map(event => {
    // Extract the main event type (e.g., 'invoice' from 'invoice.paid')
    const parts = event.event_type?.split('.');
    return parts && parts.length > 0 ? parts[0] : '';
  }))).filter(Boolean);

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Get badge color based on event type
  const getEventTypeBadge = (eventType: string) => {
    if (!eventType) return 'default';
    
    if (eventType.startsWith('invoice')) {
      return 'bg-blue-500 hover:bg-blue-600';
    } else if (eventType.startsWith('charge')) {
      return 'bg-green-500 hover:bg-green-600';
    } else if (eventType.startsWith('subscription')) {
      return 'bg-purple-500 hover:bg-purple-600';
    } else if (eventType.startsWith('payment')) {
      return 'bg-amber-500 hover:bg-amber-600';
    } else if (eventType.startsWith('customer')) {
      return 'bg-cyan-500 hover:bg-cyan-600';
    } else {
      return 'bg-slate-500 hover:bg-slate-600';
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Stripe Webhook Events</CardTitle>
          <CardDescription>
            View and track Stripe webhook events processed by the system
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Search and filter controls */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="relative w-full sm:w-auto sm:flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground/70" />
                <Input
                  placeholder="Search events..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              <Select 
                value={eventTypeFilter} 
                onValueChange={setEventTypeFilter}
              >
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  {uniqueEventTypes.map(type => (
                    <SelectItem key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Events table */}
            {loading ? (
              <div className="py-4 text-center">
                <RefreshCw className="mx-auto h-8 w-8 animate-spin text-muted-foreground" />
                <p className="mt-2">Loading webhook events...</p>
              </div>
            ) : filteredEvents.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Event Type</TableHead>
                    <TableHead className="hidden md:table-cell">Event ID</TableHead>
                    <TableHead>Processed At</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden lg:table-cell">Message</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEvents.map((event) => (
                    <TableRow key={event.id}>
                      <TableCell>
                        <Badge className={getEventTypeBadge(event.event_type)}>
                          {event.event_type || 'Unknown'}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="font-mono text-xs truncate max-w-[120px]">
                          {event.event_id || 'N/A'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <Clock className="mr-1 h-3 w-3 text-muted-foreground" />
                          <span>{formatDate(event.processed_at)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {event.success ? (
                          <Badge variant="outline" className="border-green-500 text-green-500">
                            Success
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-red-500 text-red-500">
                            Failed
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div className="text-xs truncate max-w-[300px]">
                          {event.message || 'No message'}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="py-8 text-center border rounded-lg">
                <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-medium">No Webhook Events Found</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  {searchQuery || eventTypeFilter !== 'all'
                    ? 'No events match your search criteria'
                    : 'No webhook events have been processed yet'
                  }
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};